import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const K = 8.99e9;

export default function ElectricFieldCalculator() {
  const [charge, setCharge] = useState("");
  const [distance, setDistance] = useState("");
  const [fieldResult, setFieldResult] = useState<number | null>(null);
  const [potentialResult, setPotentialResult] = useState<number | null>(null);

  const calculate = () => {
    const q = parseFloat(charge);
    const r = parseFloat(distance);

    if (isNaN(q) || isNaN(r) || r === 0) {
      toast.error("Please enter valid values (distance must be non-zero)");
      return;
    }

    const field = K * Math.abs(q) / (r * r);
    const potential = K * q / r;
    
    setFieldResult(field);
    setPotentialResult(potential);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setCharge("");
    setDistance("");
    setFieldResult(null);
    setPotentialResult(null);
  };

  return (
    <PageLayout calculatorName="Electric Field Calculator" calculatorPath="/calculator/electric-field">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Electric Field & Potential Calculator</h1>
          <p className="text-muted-foreground">
            Calculate electric field and potential from a point charge
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>E = kq/r² | V = kq/r</CardTitle>
            <CardDescription>
              Electric field (V/m or N/C) and potential (V)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="charge">Charge (C)</Label>
                <Input
                  id="charge"
                  type="number"
                  step="any"
                  placeholder="Enter charge (e.g., 1e-6)"
                  value={charge}
                  onChange={(e) => setCharge(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="distance">Distance (m)</Label>
                <Input
                  id="distance"
                  type="number"
                  step="0.01"
                  placeholder="Enter distance from charge"
                  value={distance}
                  onChange={(e) => setDistance(e.target.value)}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={calculate} className="flex-1">
                <Calculator className="mr-2 h-4 w-4" />
                Calculate
              </Button>
              <Button onClick={reset} variant="outline">
                Reset
              </Button>
            </div>

            {fieldResult !== null && potentialResult !== null && (
              <>
                <div className="space-y-4">
                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardHeader>
                      <CardTitle className="text-xl">Electric Field</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-primary">
                        {fieldResult.toExponential(3)} N/C
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Magnitude of E-field
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardHeader>
                      <CardTitle className="text-xl">Electric Potential</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-primary">
                        {potentialResult.toExponential(3)} V
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Voltage at point
                      </p>
                    </CardContent>
                  </Card>
                </div>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["electric-field"]} trigger={fieldResult} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">Key Concepts</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>• Electric field points away from + charges, toward - charges</p>
                <p>• Field strength decreases with r²</p>
                <p>• Potential is scalar, field is vector</p>
                <p>• Relationship: E = -dV/dr</p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
